import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../contexts/ContentContext';
import { Link } from 'react-router-dom';
import SchemaComponent from '../components/SchemaComponent';
import ImageCarousel from '../components/ImageCarousel';
import { sendLaptopDetailsViaWhatsApp } from '../utils/whatsappSender';
import ComputerIcon from '@mui/icons-material/Computer';
import BuildIcon from '@mui/icons-material/Build';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import MemoryIcon from '@mui/icons-material/Memory';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import PowerIcon from '@mui/icons-material/Power';
import BoltIcon from '@mui/icons-material/Bolt';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';
import ShieldIcon from '@mui/icons-material/Shield';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import './Home.css';

const LAPTOP_CONDITIONS = { all: 'all', new: 'new', refurbished: 'refurbished', used: 'used' };
const BUILD_PC_CATEGORY_ICONS = {
  cpu: MemoryIcon,
  gpu: DeveloperBoardIcon,
  ram: MemoryIcon,
  storage: StorageIcon,
  cooling: AcUnitIcon,
  psu: PowerIcon,
};
const DEFAULT_BUILD_CATEGORIES = [
  { id: 'cpu', labelAr: 'المعالج', labelEn: 'CPU', icon: 'cpu' },
  { id: 'gpu', labelAr: 'البطاقة الرسومية', labelEn: 'GPU', icon: 'gpu' },
  { id: 'ram', labelAr: 'الذاكرة', labelEn: 'RAM', icon: 'ram' },
  { id: 'storage', labelAr: 'التخزين', labelEn: 'Storage', icon: 'storage' },
  { id: 'cooling', labelAr: 'التبريد', labelEn: 'Cooling', icon: 'cooling' },
  { id: 'psu', labelAr: 'مصدر الطاقة', labelEn: 'PSU', icon: 'psu' },
];

const serviceIconMap = {
  repair: BuildIcon,
  dataRecovery: StorageIcon,
  performance: SpeedIcon,
  virus: SecurityIcon,
  customBuilds: MemoryIcon,
  diagnostics: ComputerIcon,
};

const Home = () => {
  const { language } = useLanguage();
  const { content } = useContent();
  const [images, setImages] = useState({
    hero: '',
    brands: []
  });
  const [video, setVideo] = useState({
    type: 'youtube',
    youtubeUrl: '',
    uploadedVideo: '',
    thumbnail: ''
  });
  const [laptops, setLaptops] = useState([]);
  const [laptopFilter, setLaptopFilter] = useState(LAPTOP_CONDITIONS.all);
  const [laptopSchema, setLaptopSchema] = useState(null);
  const [gamingPcConfig, setGamingPcConfig] = useState({
    baseCost: 299,
    baseCostLabelAr: '',
    baseCostLabelEn: '',
    orderButtonAr: '',
    orderButtonEn: '',
    disclaimerAr: '',
    disclaimerEn: '',
    categories: [],
    options: { cpu: [], gpu: [], ram: [], storage: [], cooling: [], psu: [] },
  });
  const [buildPcTab, setBuildPcTab] = useState(0);
  const [buildPcSelected, setBuildPcSelected] = useState({});
  const [carouselLaptop, setCarouselLaptop] = useState(null);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [startX, setStartX] = useState(0);

  useEffect(() => {
    fetchImages();
    fetchVideo();
  }, []);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/laptops`).then((r) => r.json()).then((d) => setLaptops(Array.isArray(d) ? d : [])).catch(() => setLaptops([]));
  }, []);

  // Generate Product Schema for Laptops
  useEffect(() => {
    if (laptops && laptops.length > 0) {
      const productSchemas = laptops.map(laptop => ({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": laptop.name || "Premium Laptop",
        "description": `${laptop.brand} ${laptop.name} - ${laptop.cpu} | ${laptop.ram}`,
        "image": laptop.image || "https://alrasheedcomputers.com/default-laptop.jpg",
        "brand": {
          "@type": "Brand",
          "name": laptop.brand || "Al-Rasheed"
        },
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "SAR",
          "price": (laptop.price || "").replace(/[^\d.]/g, '') || "0",
          "availability": "https://schema.org/InStock",
          "condition": laptop.condition === 'used' ? 'https://schema.org/UsedCondition' : 
                      laptop.condition === 'refurbished' ? 'https://schema.org/RefurbishedCondition' : 
                      'https://schema.org/NewCondition'
        },
        "sku": laptop.id ? `laptop-${laptop.id}` : undefined,
        "url": "https://alrasheedcomputers.com/#premium-laptops"
      }));

      // Create a CollectionPage schema with multiple products
      const collectionSchema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Premium Laptops",
        "description": "Quality pre-owned and new laptops at unbeatable prices",
        "url": "https://alrasheedcomputers.com/#premium-laptops",
        "mainEntity": productSchemas
      };

      setLaptopSchema(collectionSchema);
    }
  }, [laptops]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/gaming-pc`).then((r) => r.json()).then((data) => {
      // Ensure options has all required keys
      const normalizedData = {
        ...data,
        options: {
          cpu: data.options?.cpu || [],
          gpu: data.options?.gpu || [],
          ram: data.options?.ram || [],
          storage: data.options?.storage || [],
          cooling: data.options?.cooling || [],
          psu: data.options?.psu || [],
        },
      };
      setGamingPcConfig(normalizedData);
    }).catch(() => { });
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/images`);
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }
    } catch (error) {
      console.error('Failed to fetch images:', error);
    }
  };

  const fetchVideo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/video`);
      if (response.ok) {
        const data = await response.json();
        setVideo(data);
      }
    } catch (error) {
      console.error('Failed to fetch video:', error);
    }
  };

  // Carousel functions
  const carouselImages = images.gallery && images.gallery.length > 0 ? images.gallery : [];

  // Calculate how many slides we can show based on screen size
  const getVisibleSlides = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 900) return 3; // Desktop: 3 images
      if (window.innerWidth >= 600) return 2; // Tablet: 2 images
    }
    return 1; // Mobile: 1 image
  };

  const [visibleSlides, setVisibleSlides] = React.useState(getVisibleSlides());

  React.useEffect(() => {
    const handleResize = () => setVisibleSlides(getVisibleSlides());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, carouselImages.length - visibleSlides);

  const handlePrevSlide = () => {
    setCarouselIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextSlide = () => {
    setCarouselIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    const endX = e.changedTouches[0].clientX;
    if (startX - endX > 50) {
      handleNextSlide();
    } else if (endX - startX > 50) {
      handlePrevSlide();
    }
  };

  const t = content[language] || content.ar;

  const getServiceIcon = (iconKey) => {
    const Icon = serviceIconMap[iconKey] || ComputerIcon;
    return <Icon sx={{ fontSize: 60 }} />;
  };

  return (
    <Box>
      {/* Inject Product Schema for Search Engines */}
      <SchemaComponent schema={laptopSchema} id="laptop-products-schema" />
      <ImageCarousel
        images={carouselLaptop?.image ? [carouselLaptop.image] : []}
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        laptopName={carouselLaptop?.name}
      />
      <Box
        sx={{
          minHeight: { xs: '60vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: images.hero
            ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${images.hero})`
            : 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          color: 'white',
          py: { xs: 4, md: 8 },
          px: { xs: 2, md: 0 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: { xs: 2, md: 3 },
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                lineHeight: 1.2,
              }}
            >
              {t.heroTitle}
            </Typography>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                mb: { xs: 2, md: 3 },
                fontSize: { xs: '1rem', sm: '1.5rem', md: '2rem' },
                fontWeight: 400,
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                lineHeight: 1.4,
              }}
            >
              {t.heroSubtitle}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 3, md: 4 },
                maxWidth: '800px',
                mx: 'auto',
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                lineHeight: 1.8,
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                px: { xs: 2, sm: 0 },
              }}
            >
              {t.heroDescription}
            </Typography>
            <Button
              component={Link}
              to="/services"
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: { xs: 3, md: 4 },
                py: { xs: 1, md: 1.5 },
                fontSize: { xs: '0.875rem', md: '1.125rem' },
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'grey.100',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s',
              }}
            >
              {t.ctaButton}
            </Button>
          </Box>
        </Container>
      </Box>

      

      {/* Services Section - Right after Hero */}
      {/* <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              mb: 2,
              color: 'primary.main'
            }}
          >
            {t.servicesTitle}
          </Typography>
          <Typography 
            variant="h5" 
            align="center" 
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            {t.servicesSubtitle}
          </Typography>
          <Grid container spacing={4} sx={{ direction: 'ltr' }}>
            {t.services?.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex', minWidth: 0 }}>
                <Card 
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'center',
                    p: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      color: 'primary.main',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {getServiceIcon(service.icon)}
                  </Box>
                  <CardContent>
                    <Typography 
                      variant="h5" 
                      component="h3" 
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {service.title}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{ lineHeight: 1.8 }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              component={Link}
              to="/services"
              variant="contained"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
              }}
            >
              {language === 'ar' ? 'عرض جميع الخدمات' : 'View All Services'}
            </Button>
          </Box>
        </Container>
      </Box> */}

      {/* <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" component="h2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t.servicesTitle}
          </Typography>
          <Typography variant="h6" color="textSecondary">
            {t.servicesSubtitle}
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">

          {t.services?.map((service, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 3,
                  boxShadow: 3
                }}
              >
                <Box sx={{ color: 'success.main', mb: 2, fontSize: '3rem' }}>
                  {getServiceIcon(service.icon)}
                </Box>

                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {service.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}

        </Grid>

        <Box textAlign="center" mt={6}>
          <Button variant="outlined" color="success" size="large">
            {language === 'ar' ? 'عرض جميع الخدمات' : 'View All Services'}
          </Button>
        </Box>

      </Container> */}


      {/* Gaming PC Section - Text Only */}
      <Box sx={{ py: { xs: 4, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container alignItems="center" spacing={{ xs: 2, md: 4 }}>

            {/* Left Side - Text */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{ fontWeight: 700, mb: 2, color: 'primary.main', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}
              >
                {t.gamingTitle}
              </Typography>
              <Typography variant="h5" color="text.secondary" sx={{ mb: 4, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t.gamingSubtitle}
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: '600px', lineHeight: 1.9, fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }}
              >
                {t.gamingDescription}
              </Typography>
            </Grid>

            {/* Right Side - Image */}
            <Grid item xs={12} md={6} sx={{ position: 'relative', textAlign: 'center' }}>
              <Box
                component="img"
                src="https://media3.giphy.com/media/v1.Y2lkPTc5MGI3NjExaXg5dmd4NjRtbGptOGhkcGNoZHVtc210cGE4cHk2dzU5Zjg5dmp3OSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/znym5DlNtuRHQm2fXi/giphy.gif"
                alt="Gaming PC"
                sx={{
                  maxWidth: { xs: '100%', sm: '400px', md: '460px' },
                  width: '100%',
                  height: { xs: '250px', sm: '300px', md: '320px' },
                  borderRadius: 2,
                  boxShadow: 3,
                  margin: { xs: '0', md: '0 30px' },
                  position: 'relative',
                  zIndex: 1
                }}
              />
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* Custom PC Gallery Carousel Section */}
      <Box sx={{ py: { xs: 4, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'primary.main',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
            }}
          >
            {t.galleryTitle || (language === 'ar' ? 'معرض أجهزتنا المخصصة' : 'Our Custom PC Gallery')}
          </Typography>
          <Typography
            variant="h5"
            align="center"
            color="text.secondary"
            sx={{ mb: 4, fontSize: { xs: '1rem', sm: '1.25rem' }, px: { xs: 2, sm: 0 } }}
          >
            {t.gallerySubtitle || (language === 'ar' ? 'تصفح معرض أجهزتنا الرائعة - جرب التمرير على الجوال' : 'Browse our stunning custom builds - Swipe on mobile')}
          </Typography>

          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              px: { xs: 0, sm: 2, md: 4 },
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Carousel Container */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 2, sm: 3, md: 4 },
                transition: 'transform 0.5s ease-in-out',
                transform: `translateX(calc(-${carouselIndex * (100 / visibleSlides)}% - ${carouselIndex * (visibleSlides === 1 ? 16 : visibleSlides === 2 ? 24 : 32)}px))`,
              }}
            >
              {carouselImages.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    minWidth: {
                      xs: '100%',
                      sm: `calc(50% - 12px)`,
                      md: `calc(33.333% - 21.333px)`
                    },
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    aspectRatio: '4 / 3',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt={`Custom PC ${index + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.4s',
                    }}
                  />
                  {/* Gradient overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                      display: 'flex',
                      alignItems: 'flex-end',
                      p: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                      }}
                    >
                      {language === 'ar' ? `${t.galleryBuildLabel || 'جهاز مخصص'} #${index + 1}` : `${t.galleryBuildLabel || 'Custom Build'} #${index + 1}`}
                    </Typography>
                  </Box>
                  {/* Image counter badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 0.75,
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      fontWeight: 700,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box component="span" sx={{ opacity: 0.8 }}>{index + 1}</Box>
                    <Box component="span" sx={{ opacity: 0.6 }}>/</Box>
                    <Box component="span" sx={{ opacity: 0.8 }}>{carouselImages.length}</Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Navigation Buttons */}
            {carouselIndex > 0 && (
              <Button
                onClick={handlePrevSlide}
                sx={{
                  position: 'absolute',
                  left: { xs: -8, sm: -12, md: -16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  bgcolor: 'primary.main',
                  color: 'white',
                  minWidth: { xs: 48, sm: 56, md: 64 },
                  height: { xs: 48, sm: 56, md: 64 },
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(37, 211, 102, 0.4)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-50%) scale(1.15)',
                    boxShadow: '0 12px 32px rgba(37, 211, 102, 0.6)',
                  },
                  '&:active': {
                    transform: 'translateY(-50%) scale(1.05)',
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: { xs: 28, sm: 32, md: 36 } }} />
              </Button>
            )}

            {carouselIndex < maxIndex && (
              <Button
                onClick={handleNextSlide}
                sx={{
                  position: 'absolute',
                  right: { xs: -8, sm: -12, md: -16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  bgcolor: 'primary.main',
                  color: 'white',
                  minWidth: { xs: 48, sm: 56, md: 64 },
                  height: { xs: 48, sm: 56, md: 64 },
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(37, 211, 102, 0.4)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-50%) scale(1.15)',
                    boxShadow: '0 12px 32px rgba(37, 211, 102, 0.6)',
                  },
                  '&:active': {
                    transform: 'translateY(-50%) scale(1.05)',
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: { xs: 28, sm: 32, md: 36 } }} />
              </Button>
            )}

            {/* Dot Indicators */}
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
              }}
            >
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCarouselIndex(index)}
                  sx={{
                    width: carouselIndex === index ? 32 : 12,
                    height: 12,
                    borderRadius: 6,
                    bgcolor: carouselIndex === index ? 'primary.main' : 'grey.300',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: carouselIndex === index ? 'primary.dark' : 'grey.400',
                    },
                  }}
                />
              ))}
            </Box>

            {/* Progress Bar */}
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              {/* <Box
                sx={{
                  flex: 1,
                  height: 8,
                  bgcolor: 'grey.200',
                  borderRadius: 4,
                  overflow: 'hidden',
                  maxWidth: 300,
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    bgcolor: 'primary.main',
                    borderRadius: 4,
                    transition: 'width 0.5s ease-in-out',
                    width: `${((carouselIndex + 1) / (maxIndex + 1)) * 100}%`,
                  }}
                />
              </Box> */}
              {/* <Typography
                variant="body2"
                sx={{
                  minWidth: 60,
                  textAlign: 'center',
                  fontWeight: 700,
                  color: 'primary.main',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                {carouselIndex + 1} / {maxIndex + 1}
              </Typography> */}
            </Box>
          </Box>

          {/* Carousel Info */}
          {/* <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 1, fontWeight: 500 }}
            >
              {language === 'ar' 
                ? `📱 ${t.galleryShowing || 'يعرض'} ${visibleSlides === 3 ? (t.galleryThreeImages || '3 صور') : visibleSlides === 2 ? (t.galleryTwoImages || 'صورتين') : (t.galleryImage || 'صورة واحدة')} · ${t.galleryInfo || 'اسحب أو استخدم الأزرار للتنقل'}` 
                : `📱 ${t.galleryShowing || 'Showing'} ${visibleSlides} ${visibleSlides > 1 ? (t.galleryImages || 'images') : (t.galleryImage || 'image')} · ${t.galleryInfo || 'Swipe or use arrows to navigate'}`}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: 'block', opacity: 0.7 }}
            >
              {language === 'ar' ? `${carouselImages.length} ${t.galleryImagesAvailable || 'صور متاحة في المعرض'}` : `${carouselImages.length} ${t.galleryImagesAvailable || 'images available in gallery'}`}
            </Typography>
          </Box> */}
        </Container>
      </Box>

      <Container maxWidth={false} sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>

        {/* --- 1. NEW SERVICE HEADER (Place this BEFORE the Grid) --- */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              color: 'success.main',
              fontWeight: 'bold',
              mb: 2,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            }}
          >
            {t.servicesTitle}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto',
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
              px: { xs: 2, sm: 0 },
            }}
          >
            {t.servicesSubtitle}
          </Typography>
        </Box>


        {/* --- 2. SERVICES GRID (3 Items in a Row) --- */}
        <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
          {t.services?.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: { xs: 2, md: 3 },
                  boxShadow: 3,
                  borderRadius: 2
                }}
              >
                <Box sx={{ color: 'success.main', fontSize: { xs: '2rem', md: '3rem' }, mb: 2 }}>
                  {getServiceIcon(service.icon)}
                </Box>
                <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    {service.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

      </Container>

      {/* Premium Laptops - Full section on home */}
      <Box id="premium-laptops" sx={{ py: { xs: 4, md: 8 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" align="center" gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'primary.main', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
            {t.premiumLaptopsTitle}
          </Typography>
          <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 4, fontSize: { xs: '1rem', sm: '1.25rem' }, px: { xs: 2, sm: 0 } }}>
            {t.premiumLaptopsSubtitle}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 4, px: { xs: 1, sm: 0 } }}>
            {Object.entries(LAPTOP_CONDITIONS).map(([key, value]) => (
              <Button
                key={key}
                variant={laptopFilter === value ? 'contained' : 'outlined'}
                onClick={() => setLaptopFilter(value)}
                sx={{ textTransform: 'none', borderRadius: 10, fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 2, sm: 3 } }}
              >
                {language === 'ar' ? { all: 'الكل', new: 'جديد', refurbished: 'معاد تجديده', used: 'مستعمل' }[key] : { all: 'All', new: 'New', refurbished: 'Refurbished', used: 'Used' }[key]}
              </Button>
            ))}
          </Box>
          <Grid container spacing={{ xs: 2, md: 4 }}>
            {(laptopFilter === LAPTOP_CONDITIONS.all ? laptops : laptops.filter((item) => (item.condition || '').toLowerCase() === laptopFilter)).map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={item.id || index}>
                <Card elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', transition: 'box-shadow 0.3s, transform 0.3s', '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' } }}>
                  <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={() => { setCarouselLaptop(item); setCarouselOpen(true); }}>
                    <CardMedia component="img" height="220" image={item.image || ''} alt={item.name} sx={{ objectFit: 'cover', bgcolor: 'grey.200' }} />
                    {item.condition && (
                      <Chip label={language === 'ar' ? { new: 'جديد', refurbished: 'معاد تجديده', used: 'مستعمل' }[item.condition] || item.condition : item.condition} size="small" sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 600, textTransform: 'capitalize', fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                    )}
                  </Box>
                  <CardContent sx={{ flex: 1, p: { xs: 1.5, md: 2.5 } }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{item.brand}</Typography>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }}>{item.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
                      <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700, fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }}>{item.price}</Typography>
                      {item.originalPrice && <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{item.originalPrice}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {item.cpu && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><MemoryIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{item.cpu}</Typography></Box>}
                      {item.ram && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><StorageIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{item.ram}</Typography></Box>}
                      {item.storage && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><StorageIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{item.storage}</Typography></Box>}
                      {item.display && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><DesktopWindowsIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{item.display}</Typography></Box>}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button variant="contained" fullWidth size="small" onClick={() => sendLaptopDetailsViaWhatsApp(item)} sx={{ textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{language === 'ar' ? 'اشتري الآن' : 'Buy Now'}</Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {(!laptops.length || (laptopFilter !== LAPTOP_CONDITIONS.all && !laptops.filter((item) => (item.condition || '').toLowerCase() === laptopFilter).length)) && (
            <Typography align="center" color="text.secondary" sx={{ py: 6, fontSize: { xs: '0.875rem', sm: '1rem' } }}>{language === 'ar' ? 'لا توجد أجهزة لعرضها. أضف من لوحة التحكم.' : 'No laptops to show. Add from admin panel.'}</Typography>
          )}
        </Container>
      </Box>

      {/* Brand Logos Section */}
      {images.brands && images.brands.length > 0 && (
        <Box sx={{ py: { xs: 4, md: 8 }, bgcolor: 'background.default' }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              component="h2"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 4,
                color: 'primary.main',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              {language === 'ar' ? 'العلامات التجارية' : 'Our Brands'}
            </Typography>
            <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center" alignItems="center">
              {images.brands.map((brand, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Card
                    sx={{
                      p: { xs: 2, md: 3 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: { xs: 100, sm: 120, md: 150 },
                      transition: 'transform 0.3s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={brand}
                      alt={`Brand ${index + 1}`}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: { xs: '60px', sm: '80px', md: '100px' },
                        objectFit: 'contain',
                      }}
                    />
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Build Your Gaming PC - Full section on home */}
      <Box id="build-pc" sx={{ py: { xs: 4, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" align="center" gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'primary.main', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
            {t.buildPcTitle}
          </Typography>
          <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 4, fontSize: { xs: '1rem', sm: '1.25rem' }, px: { xs: 2, sm: 0 } }}>
            {t.buildPcSubtitle}
          </Typography>
          {(() => {
            const categories = gamingPcConfig.categories?.length ? gamingPcConfig.categories : DEFAULT_BUILD_CATEGORIES;
            const categoryIds = categories.map((c) => c.id);
            const activeCategoryId = categoryIds[buildPcTab] || 'cpu';
            const options = gamingPcConfig.options?.[activeCategoryId] || [];
            const labels = (item) => (language === 'ar' ? item.labelAr : item.labelEn) || item.name;
            const baseLabel = language === 'ar' ? gamingPcConfig.baseCostLabelAr : gamingPcConfig.baseCostLabelEn;
            const orderLabel = language === 'ar' ? gamingPcConfig.orderButtonAr : gamingPcConfig.orderButtonEn;
            const disclaimer = language === 'ar' ? gamingPcConfig.disclaimerAr : gamingPcConfig.disclaimerEn;
            const total = (gamingPcConfig.baseCost || 0) + Object.keys(buildPcSelected).reduce((sum, key) => sum + (buildPcSelected[key]?.price ? Number(buildPcSelected[key].price) : 0), 0);
            const perfLabels = language === 'ar' ? { good: 'جيد', great: 'رائع', excellent: 'ممتاز', extreme: 'متطرف' } : { good: 'Good', great: 'Great', excellent: 'Excellent', extreme: 'Extreme' };
            return (
              <Grid container spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={7}>
                  <Tabs value={buildPcTab} onChange={(_, v) => setBuildPcTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    {categories.map((cat) => {
                      const Icon = BUILD_PC_CATEGORY_ICONS[cat.icon] || MemoryIcon;
                      return <Tab key={cat.id} icon={<Icon />} iconPosition="start" label={labels(cat)} sx={{ textTransform: 'none', minHeight: 48, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />;
                    })}
                  </Tabs>
                  <Grid container spacing={{ xs: 1, md: 2 }}>
                    {options.map((opt, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Card variant="outlined" sx={{ cursor: 'pointer', border: 2, borderColor: buildPcSelected[activeCategoryId]?.name === opt.name ? 'primary.main' : 'transparent', borderRadius: 2, transition: 'all 0.2s', '&:hover': { borderColor: 'primary.light' } }} onClick={() => setBuildPcSelected((prev) => ({ ...prev, [activeCategoryId]: opt }))}>
                          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, p: { xs: 1.5, md: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
                              {React.createElement(BUILD_PC_CATEGORY_ICONS[activeCategoryId] || MemoryIcon, { sx: { color: 'primary.main', fontSize: { xs: 20, md: 28 } } })}
                              <Box>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{opt.name}</Typography>
                                {opt.performance && <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{perfLabels[opt.performance] || opt.performance}</Typography>}
                              </Box>
                            </Box>
                            <Typography variant="h6" color="primary.main" fontWeight={700} sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }}>{opt.price ? (typeof opt.price === 'number' ? `$${opt.price}` : opt.price) : '-'}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  {!options.length && <Typography color="text.secondary" sx={{ mt: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>{language === 'ar' ? 'أضف مكونات من لوحة التحكم.' : 'Add components from admin panel.'}</Typography>}
                </Grid>
                <Grid item xs={12} md={5}>
                  <Card sx={{ position: { md: 'sticky' }, top: 100, borderRadius: 2 }}>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>{language === 'ar' ? 'تجميعك' : 'Your Build'}</Typography>
                      {categoryIds.map((id) => (
                        <Box key={id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{categories.find((c) => c.id === id) ? labels(categories.find((c) => c.id === id)) : id}:</Typography>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{buildPcSelected[id]?.name || '-'}</Typography>
                        </Box>
                      ))}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{baseLabel || (language === 'ar' ? 'العلبة + التجميع' : 'Case + Assembly')}:</Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>${gamingPcConfig.baseCost || 0}</Typography>
                      </Box>
                      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>{language === 'ar' ? 'المجموع' : 'Total'}:</Typography>
                          <Typography variant="h5" color="primary.main" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>${total}</Typography>
                        </Box>
                      </Box>
                      <Button variant="contained" fullWidth size="large" startIcon={<BoltIcon />} sx={{ mt: 3, py: { xs: 1, md: 1.5 }, textTransform: 'none', fontSize: { xs: '0.875rem', sm: '1rem' } }}>{orderLabel || (language === 'ar' ? 'طلب تجميع مخصص' : 'Order Custom Build')}</Button>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{disclaimer}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            );
          })()}
        </Container>
      </Box>




      {/* Video Section */}
      {(video.youtubeUrl || video.uploadedVideo) && (
        <Box sx={{ py: { xs: 4, md: 8 }, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              component="h2"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 4,
                color: 'primary.main',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              {language === 'ar' ? 'شاهد خدماتنا' : 'Watch Our Services'}
            </Typography>
            <Box
              sx={{
                maxWidth: '900px',
                mx: 'auto',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 6,
                px: { xs: 2, sm: 0 }
              }}
            >
              {video.type === 'youtube' && video.youtubeUrl ? (
                <Box
                  component="iframe"
                  src={video.youtubeUrl.replace('watch?v=', 'embed/')}
                  title="Service Video"
                  sx={{
                    width: '100%',
                    height: { xs: '250px', sm: '400px', md: '500px' },
                    border: 'none',
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : video.uploadedVideo ? (
                <Box
                  component="video"
                  controls
                  poster={video.thumbnail || ''}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                >
                  <source src={video.uploadedVideo} type="video/mp4" />
                  {language === 'ar' ? 'متصفحك لا يدعم تشغيل الفيديو' : 'Your browser does not support the video tag'}
                </Box>
              ) : null}
            </Box>
          </Container>
        </Box>
      )}

      {/* Interactive Features Cards Section */}
      <Box sx={{ py: { xs: 4, md: 8 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'primary.main',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
            }}
          >
            {t.interactiveCards?.title || (language === 'ar' ? 'لماذا تختارنا؟' : 'Why Choose Us?')}
          </Typography>
          <Typography
            variant="h5"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, fontSize: { xs: '1rem', sm: '1.25rem' }, px: { xs: 2, sm: 0 } }}
          >
            {t.interactiveCards?.subtitle || (language === 'ar' ? 'اكتشف المزايا التي تميز خدماتنا' : 'Discover what sets our services apart')}
          </Typography>

          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Card 1 - Expert Team */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    '& .card-icon': {
                      transform: 'scale(1.2) rotate(10deg)',
                      color: 'white',
                    },
                    '& .card-title': {
                      color: 'white',
                    },
                    '& .card-description': {
                      color: 'white',
                    }
                  }
                }}
              >
                <Box
                  className="card-icon"
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    transition: 'all 0.3s',
                  }}
                >
                  <BuildIcon sx={{ fontSize: 60 }} />
                </Box>
                <Typography
                  variant="h6"
                  className="card-title"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    transition: 'color 0.3s',
                  }}
                >
                  {t.interactiveCards?.cards?.[0]?.title || (language === 'ar' ? 'خبرة وجودة' : 'Expertise & Quality')}
                </Typography>
                <Typography
                  className="card-description"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    lineHeight: 1.6,
                    transition: 'color 0.3s',
                  }}
                >
                  {t.interactiveCards?.cards?.[0]?.description || (language === 'ar' ? 'سنوات من الخبرة في مجال الكمبيوتر والتقنية' : 'Years of experience in computers and technology')}
                </Typography>
              </Card>
            </Grid>

            {/* Card 2 - Fast Service */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                    background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                    '& .card-icon': {
                      transform: 'scale(1.2) rotate(10deg)',
                      color: 'white',
                    },
                    '& .card-title': {
                      color: '#333',
                    },
                    '& .card-description': {
                      color: '#555',
                    }
                  }
                }}
              >
                <Box
                  className="card-icon"
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    transition: 'all 0.3s',
                  }}
                >
                  <SpeedIcon sx={{ fontSize: 60 }} />
                </Box>
                <Typography
                  variant="h6"
                  className="card-title"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    transition: 'color 0.3s',
                  }}
                >
                  {t.interactiveCards?.cards?.[1]?.title || (language === 'ar' ? 'أسعار منافسة' : 'Competitive Prices')}
                </Typography>
                <Typography
                  className="card-description"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    lineHeight: 1.6,
                    transition: 'color 0.3s',
                  }}
                >
                  {t.interactiveCards?.cards?.[1]?.description || (language === 'ar' ? 'أفضل الأسعار مع ضمان الجودة العالية' : 'Best prices with guaranteed high quality')}
                </Typography>
              </Card>
            </Grid>

            {/* Card 3 - Reliable */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    '& .card-icon': {
                      transform: 'scale(1.2) rotate(10deg)',
                      color: 'white',
                    },
                    '& .card-title': {
                      color: 'white',
                    },
                    '& .card-description': {
                      color: 'white',
                    }
                  }
                }}
              >
                <Box
                  className="card-icon"
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    transition: 'all 0.3s',
                  }}
                >
                  <ShieldIcon sx={{ fontSize: 60 }} />
                </Box>
                <Typography
                  variant="h6"
                  className="card-title"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    transition: 'color 0.3s',
                  }}
                >
                  {t.interactiveCards?.cards?.[2]?.title || (language === 'ar' ? 'خدمة سريعة' : 'Fast Service')}
                </Typography>
                <Typography
                  className="card-description"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    lineHeight: 1.6,
                    transition: 'color 0.3s',
                  }}
                >
                  {t.interactiveCards?.cards?.[2]?.description || (language === 'ar' ? 'نقدم خدمات سريعة وفعالة لجميع عملائنا' : 'We provide fast and efficient service to all our customers')}
                </Typography>
              </Card>
            </Grid>

            {/* Card 4 - Affordable */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                    background: 'linear-gradient(135deg, #fba2c6 0%, #cd84b1 100%)',
                    '& .card-icon': {
                      transform: 'scale(1.2) rotate(10deg)',
                      color: 'white',
                    },
                    '& .card-title': {
                      color: 'white',
                    },
                    '& .card-description': {
                      color: 'white',
                    }
                  }
                }}
              >
                <Box
                  className="card-icon"
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    transition: 'all 0.3s',
                  }}
                >
                  <StarIcon sx={{ fontSize: 60 }} />
                </Box>
                <Typography
                  variant="h6"
                  className="card-title"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    transition: 'color 0.3s',
                  }}
                >
                  {t.interactiveCards?.cards?.[3]?.title || (language === 'ar' ? 'رضا العملاء' : 'Customer Satisfaction')}
                </Typography>
                <Typography
                  className="card-description"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    lineHeight: 1.6,
                    transition: 'color 0.3s',
                  }}
                >
                  {t.interactiveCards?.cards?.[3]?.description || (language === 'ar' ? 'رضا العملاء هو أولويتنا الأولى وأساس نجاحنا' : 'Customer satisfaction is our top priority and the foundation of our success')}
                </Typography>
              </Card>
            </Grid>

            {/* Card 5 - Support */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                    background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                    '& .card-icon': {
                      transform: 'scale(1.2) rotate(10deg)',
                      color: 'white',
                    },
                    '& .card-title': {
                      color: 'white',
                    },
                    '& .card-description': {
                      color: 'white',
                    }
                  }
                }}
              >
                <Box
                  className="card-icon"
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    transition: 'all 0.3s',
                  }}
                >
                  <EmojiEventsIcon sx={{ fontSize: 60 }} />
                </Box>
                <Typography
                  variant="h6"
                  className="card-title"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    transition: 'color 0.3s',
                  }}
                >
                  {language === 'ar' ? 'دعم ممتاز' : 'Excellent Support'}
                </Typography>
                <Typography
                  className="card-description"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    lineHeight: 1.6,
                    transition: 'color 0.3s',
                  }}
                >
                  {language === 'ar' ? 'فريق دعم العملاء متاح للإجابة على استفسارتك' : '24/7 customer support for your queries'}
                </Typography>
              </Card>
            </Grid>

            {/* Card 6 - Satisfaction */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: 'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                    background: 'linear-gradient(135deg, #ffa502 0%, #ff6348 100%)',
                    '& .card-icon': {
                      transform: 'scale(1.2) rotate(10deg)',
                      color: 'white',
                    },
                    '& .card-title': {
                      color: 'white',
                    },
                    '& .card-description': {
                      color: 'white',
                    }
                  }
                }}
              >
                <Box
                  className="card-icon"
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    transition: 'all 0.3s',
                  }}
                >
                  <FavoriteBorderIcon sx={{ fontSize: 60 }} />
                </Box>
                <Typography
                  variant="h6"
                  className="card-title"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    transition: 'color 0.3s',
                  }}
                >
                  {language === 'ar' ? 'رضا العملاء' : 'Customer Satisfaction'}
                </Typography>
                <Typography
                  className="card-description"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    lineHeight: 1.6,
                    transition: 'color 0.3s',
                  }}
                >
                  {language === 'ar' ? 'رضا العملاء هو أولويتنا الأولى وأساس نجاحنا' : 'Customer satisfaction is our top priority'}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      
    </Box>
  );
};

export default Home;
